import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PositionsQuery } from '../../../features/positions/state/positions.query';
import {
    debounceTime,
    distinctUntilChanged,
    filter,
    map,
    shareReplay,
    startWith,
    switchMap,
    take,
    takeUntil,
    tap
} from 'rxjs/operators';
import { AccountIdConcatPipe } from '../sb-account-list/account-id-concat.pipe';
import { CountryCodePipe } from '../../pipes/country-code.pipe';
import { HighlightPipe } from '../../../features/equity-search/search.highlight.pipe';
import { MatLegacyMenuModule as MatMenuModule } from '@angular/material/legacy-menu';
import { MatLegacyPaginatorModule as MatPaginatorModule } from '@angular/material/legacy-paginator';
import { SbAnimatedNumberComponent } from '../sb-animated-number/sb-animated-number.component';
import {
    Badge,
    BadgesChangeEvent,
    SbBadgesComponent
} from '../sb-badges/sb-badges.component';
import { SbCompanyLogoComponent } from '../sb-company-logo/sb-company-logo.component';
import { SbGenericTypeSelectComponent } from '../../../pages/authenticated/accounting/components/sb-generic-type-select/sb-generic-type-select.component';
import { SbNotificationBadgeComponent } from '../sb-notification-badge/sb-notification-badge.component';
import { TablerIconsModule } from 'angular-tabler-icons';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { UniquePipe } from '../../pipes/unique.pipe';
import { combineLatest, Observable, Subject } from 'rxjs';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { UiQuery } from '../../state/ui/ui.query';
import {
    fadeInOut,
    fadeInOutModal,
    list,
    slideInOut
} from '../../animations/fade-in-out.animation';
import { MatLegacyTooltipModule as MatTooltipModule } from '@angular/material/legacy-tooltip';
import { AccountTypeEnum, Acct } from '../../state/accts/acct.model';
import { accumulateByGroup } from '../../../features/aggregation/aggregation';
import { Position } from '../../../features/positions/state/position.model';
import { SettingsQuery } from '../../../features/settings/state/settings.query';
import { UserSettingsQuery } from '../../../features/user-settings/state/user-settings.query';
import { CompaniesQuery } from '../../../pages/authenticated/companies/state/companies.query';
import { MatLegacyFormFieldModule as MatFormFieldModule } from '@angular/material/legacy-form-field';
import { MatLegacyInputModule as MatInputModule } from '@angular/material/legacy-input';
import { MatLegacyOptionModule as MatOptionModule } from '@angular/material/legacy-core';
import { MatLegacySelectModule as MatSelectModule } from '@angular/material/legacy-select';
import { combineQueries, distinctUntilArrayItemChanged } from '@datorama/akita';
import { InstrumentsQuery } from '../../state/instruments/instruments.query';
import { Instrument, InstrumentType } from '../../state/instruments/instrument.model';
import { UserSettingsService } from '../../../features/user-settings/state/user-settings.service';
import { ComponentSettings } from '../../../features/user-settings/state/user-setting.model';
import { AcctsQuery } from '../../state/accts/accts.query';
import { AccountIssuer } from '../../../pages/admin/state/account-issuers/account-issuer.model';
import { AccountIssuersQuery } from '../../../pages/admin/state/account-issuers/account-issuers.query';
import { InferFilter } from '../../state/query-filter.functions';
import { PositionsService } from '../../../features/positions/state/positions.service';
import { SbFilterCountPipe } from '../../pipes/sb-filter-count.pipe';
import { RouterQuery } from '@datorama/akita-ng-router-store';
import { LegalEntity } from '../../../pages/admin/state/legal-entities/legal-entity.model';
import { LegalEntityQuery } from '../../../pages/admin/state/legal-entities/legal-entity.query';
import { ToastService } from '../../services/toast/toast.service';

@Component({
    selector: 'sb-top-holdings',
    standalone: true,
    imports: [
        CommonModule,
        AccountIdConcatPipe,
        CountryCodePipe,
        HighlightPipe,
        MatMenuModule,
        MatPaginatorModule,
        SbAnimatedNumberComponent,
        SbBadgesComponent,
        SbCompanyLogoComponent,
        SbGenericTypeSelectComponent,
        SbNotificationBadgeComponent,
        TablerIconsModule,
        TranslateModule,
        UniquePipe,
        ReactiveFormsModule,
        RouterLink,
        MatTooltipModule,
        MatFormFieldModule,
        MatInputModule,
        MatOptionModule,
        MatSelectModule,
        SbFilterCountPipe
    ],
    templateUrl: './sb-top-holdings.component.html',
    styleUrls: ['./sb-top-holdings.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [list, slideInOut, fadeInOut, fadeInOutModal]
})
export class SbTopHoldingsComponent implements OnInit, OnDestroy {
    private _unsubscribe$ = new Subject();
    constructor(
        private _positionsQuery: PositionsQuery,
        private _positionsService: PositionsService,
        private _ui: UiQuery,
        private _accountsQuery: AcctsQuery,
        private _accountIssuersQuery: AccountIssuersQuery,
        private _settingsQuery: SettingsQuery,
        private _router: Router,
        private _route: ActivatedRoute,
        private _userSettingsQuery: UserSettingsQuery,
        private _userSettingsService: UserSettingsService,
        private _companiesQuery: CompaniesQuery,
        private _translateService: TranslateService,
        private _instrumentsQuery: InstrumentsQuery,
        private _routerQuery: RouterQuery,
        private _legalEntityQuery: LegalEntityQuery,
        private _toastService: ToastService
    ) {}

    isLoading$ = this._positionsQuery.isLoading$;
    viewportSize$ = this._ui.viewportSize$;
    filterInstrumentTypeCtrl = new FormControl<Array<Instrument['type']>>([]);
    instrumentTypeFilter$ = this.filterInstrumentTypeCtrl.valueChanges.pipe(
        debounceTime(300),
        startWith(this.filterInstrumentTypeCtrl.value),
        distinctUntilArrayItemChanged(),
        map((types) => {
            return {
                type: {
                    in: [...types] as Array<Instrument['type']>,
                    ne: 'CASH' as Instrument['type']
                }
            };
        }),
        shareReplay(1)
    );

    sortByOptions = [
        { value: 'marketValueInBaseCurrency', label: 'Market value' },
        { value: 'acquisitionValue', label: 'Acquisition value' },
        { value: 'totalPnL', label: 'Total profit & loss (PnL)' }
        //{ value: 'dailyPerformanceRelative', label: 'Daily performance' }
    ];
    sortByCtrl = new FormControl(
        this._routerQuery.getQueryParams('sortTopBy')
            ? this.sortByOptions.find(
                  (option) =>
                      option.value === this._routerQuery.getQueryParams('sortTopBy')
              )
            : this.sortByOptions[0]
    );
    sortBy$ = this.sortByCtrl.valueChanges.pipe(
        tap((value) => {
            const queryParams = { sortTopBy: value.value };
            this._router.navigate([], {
                relativeTo: this._route,
                queryParams,
                queryParamsHandling: 'merge',
                replaceUrl: true
            });
        }),
        startWith(this.sortByCtrl.value),
        distinctUntilChanged(),
        shareReplay(1)
    );

    issuersCtrl = new FormControl<Array<AccountIssuer['accountIssuerId']>>([]);
    activeIssuerIds$ = this.issuersCtrl.valueChanges.pipe(
        debounceTime(300),
        startWith(this.issuersCtrl.value),
        distinctUntilArrayItemChanged(),
        shareReplay(1)
    );

    legalEntitiesCtrl = new FormControl<Array<LegalEntity['ownerId']>>([]);
    activeLegalEntityIds$ = this._legalEntityQuery.active$.pipe(
        map((legalEntities) => legalEntities.map((legalEntity) => legalEntity.ownerId)),
        distinctUntilArrayItemChanged()
    ); /*this.legalEntitiesCtrl.valueChanges.pipe(
        debounceTime(300),
        startWith(this.legalEntitiesCtrl.value),
        distinctUntilArrayItemChanged(),
        shareReplay(1)
    );*/

    private _topHoldings$ = combineQueries([
        combineQueries([
            this.instrumentTypeFilter$,
            combineQueries([this.activeIssuerIds$, this.activeLegalEntityIds$]).pipe(
                switchMap(([issuerIds, legalEntityIds]) => {
                    return this._accountsQuery.selectAccountsByFilter$({
                        accountType: { ne: AccountTypeEnum.BROKER },
                        accountIssuerId: {
                            in: issuerIds
                        },
                        ownerId: {
                            in: legalEntityIds
                        }
                    });
                }),
                map((accounts) => accounts.map((account) => account.accountId)),
                distinctUntilArrayItemChanged()
            )
        ]).pipe(
            switchMap(([type, accountIds]) => {
                const filters: InferFilter<Position> = {
                    instrument: {
                        ...type
                    },
                    marketValue: {
                        gt: 0
                    },
                    account: {
                        accountId: {
                            in: accountIds
                        },
                        accountType: {
                            ne: AccountTypeEnum.BROKER
                        }
                    }
                };
                return combineQueries([
                    this._positionsQuery
                        .selectPositionsByFilter$(filters)
                        .pipe(distinctUntilArrayItemChanged()),
                    this._positionsQuery.selectActiveTotalsByFilter$(filters)
                ]).pipe(debounceTime(0));
            }),
            debounceTime(0)
        ),
        this._settingsQuery.currenciesAndRates$,
        this._instrumentsQuery.allAsObject$
    ]).pipe(
        map(([[positions, totals], currenciesAndRates, instrumentsMap]) => {
            let groupedPositions = accumulateByGroup<
                Position,
                {
                    displayName: string;
                    marketValueInBaseCurrency: number;
                    acquisitionValue: number;
                    totalPnL: number;
                    dailyPerformanceRelative: number;
                    positions: Position[];
                }
            >(
                positions,
                (position, acc) => {
                    const baseConversionRate =
                        currenciesAndRates[position.position.currency];
                    const marketValueInBaseCurrency =
                        (acc?.marketValueInBaseCurrency || 0) +
                        position.marketValueInBaseCurrency;
                    const acquisitionValue =
                        (acc?.acquisitionValue || 0) +
                        position.acquisitionValue * baseConversionRate;
                    const totalPnL =
                        (acc?.totalPnL || 0) +
                        ((position.unrealizedPnLAvg || 0) +
                            (position.position.realizedPnLAvg || 0)) *
                            baseConversionRate;
                    const dailyPerformanceRelative = position.dailyPerformanceRelative;

                    const instrument = instrumentsMap[
                        position.instrument.instrumentId
                    ] as Instrument;
                    return {
                        displayName:
                            instrument?.decoratedInstrumentName || instrument?.name,
                        totalPnL,
                        marketValueInBaseCurrency,
                        acquisitionValue,
                        dailyPerformanceRelative,
                        positions: [...(acc?.positions || []), position],
                        marketValueInBaseCurrencyPercentage: 0,
                        acquisitionValuePercentage: 0,
                        totalPnLPercentage: 0
                    };
                },
                {
                    key: 'instrument.instrumentId'
                },
                false,
                false
            ) as Array<{
                displayName: string;
                marketValueInBaseCurrency: number;
                acquisitionValue: number;
                totalPnL: number;
                dailyPerformanceRelative: number;
                marketValueInBaseCurrencyPercentage: number;
                acquisitionValuePercentage: number;
                totalPnLPercentage: number;
                positions: Position[];
            }>;

            if (groupedPositions.length > 0) {
                groupedPositions = groupedPositions.map((group) => {
                    if (group.positions.length > 1) {
                        group.dailyPerformanceRelative = group.positions.reduce(
                            (acc, position) => {
                                const weight =
                                    group.marketValueInBaseCurrency === 0
                                        ? 0
                                        : position.marketValueInBaseCurrency /
                                          group.marketValueInBaseCurrency;
                                return (
                                    acc +
                                    (position.dailyPerformanceRelative ?? 0) * weight
                                );
                            },
                            0
                        );
                    }
                    group.marketValueInBaseCurrencyPercentage =
                        group.marketValueInBaseCurrency === 0
                            ? 0
                            : group.marketValueInBaseCurrency /
                              totals.marketValueInBaseCurrency;
                    group.acquisitionValuePercentage =
                        group.acquisitionValue === 0
                            ? 0
                            : group.acquisitionValue /
                              totals.acquisitionValueInBaseCurrency;
                    group.totalPnLPercentage =
                        group.totalPnL === 0
                            ? 0
                            : group.totalPnL / totals.totalPnlInBaseCurrency;
                    return group;
                });
            }
            return groupedPositions;
        }),
        debounceTime(0)
    );

    sortedTopHoldings$ = combineLatest([
        this._topHoldings$,
        this.sortBy$,
        this._companiesQuery.allAsObject$,
        this._positionsQuery.isLoading$.pipe(
            filter((isLoading) => !isLoading),
            take(1)
        )
    ]).pipe(
        debounceTime(0),
        map(([groupedPositions, sortBy, companies]) => {
            const sortByKey = sortBy.value;
            return (groupedPositions as Array<any>)
                .sort((a, b) => b[sortByKey] - a[sortByKey])
                .slice(0, 10)
                .map((position, index) => {
                    const firstPosition: Position = position.positions[0];
                    const companyInfo = companies[firstPosition.instrument?.isin];
                    const topHolding = {
                        ...position,
                        value: position[sortByKey],
                        currency: firstPosition.position.currency,
                        id: `${index}_${position.displayName}`,
                        link: null,
                        type: firstPosition.instrument?.type,
                        privateAssetType:
                            firstPosition.instrument?.instrumentData?.privateAssetType,
                        auxiliaryLink: false
                    };

                    topHolding.website = companyInfo?.website;
                    topHolding.logoUrl = companyInfo?.logoUrl;
                    topHolding.symbol = companyInfo?.symbol;
                    switch (firstPosition.instrument?.type) {
                        case 'PE_OPTION':
                        case 'PE_SHAREHOLDER_CONTRIBUTION':
                        case 'PE_CONVERTIBLE':
                        case 'PRIVATE_EQUITY':
                        case 'PE_PREF_SHARE':
                        case 'PE_OTHER_SHARE':
                            const companyInstrument =
                                this._instrumentsQuery.getInstrumentById(
                                    firstPosition.instrument?.underlyingInstrumentId
                                );
                            topHolding.link = `/private-equity/${firstPosition.instrument?.underlyingInstrumentId}`;
                            topHolding.website =
                                companyInstrument?.instrumentData?.website ??
                                topHolding.website;
                            break;
                        case 'EQUITY':
                            topHolding.link = `/equities/${firstPosition.instrument?.instrumentId}`;
                            break;
                        case 'FUND':
                            topHolding.link = `/funds/${firstPosition.instrument?.instrumentId}`;
                            break;
                        case 'ETF':
                            topHolding.link = `/funds/${firstPosition.instrument?.instrumentId}`;
                            break;
                        case 'CERT':
                            topHolding.link = `/cert/${firstPosition.instrument?.instrumentId}`;
                            break;
                        case 'PRIVATE_FUND':
                            topHolding.link = `/private-equity-fund/overview/${firstPosition.instrument?.instrumentId}`;
                            break;
                        case 'COMMERCIAL_REAL_ESTATE':
                            topHolding.link = `/commercial-real-estate(modal:action/commercial-real-estate/view/${firstPosition.instrument?.instrumentId})`;
                            topHolding.auxiliaryLink = true;
                            break;
                        case 'PRIVATE_ASSET':
                            topHolding.link = `/private-assets/overview/${firstPosition.instrument?.instrumentData?.privateAssetType}(modal:action/asset/view/${firstPosition.instrument?.instrumentId})`;
                            topHolding.auxiliaryLink = true;
                            break;
                        case 'BOND':
                            topHolding.link = `/fixed-income/${firstPosition.instrument?.instrumentId}`;
                            topHolding.auxiliaryLink = true;
                            break;
                        default:
                            break;
                    }
                    return topHolding;
                });
        })
    );

    availableLegalEntities$ = combineLatest([
        this._accountsQuery.selectAvailableLegalEntities$,
        this._legalEntityQuery.allAsObject$
    ]).pipe(
        map(([ownerIds, owners]) => {
            const currentSelection = this.legalEntitiesCtrl.value;
            return ownerIds.map((id) => {
                // if legal entity is not found in the list of legal entities then use a default legal entity object
                const legalEntity = owners[id] || {
                    displayName: 'Unknown',
                    shortName: 'Unknown',
                    longName: 'Unknown',
                    ownerId: null
                };
                return {
                    title:
                        legalEntity.displayName ||
                        legalEntity.shortName ||
                        legalEntity.longName,
                    outline: !currentSelection.includes(legalEntity.ownerId),
                    data: legalEntity
                };
            });
        })
    );

    availableIssuers$ = combineLatest([
        this._accountsQuery.selectAvailableAccountIssuers$,
        this._accountIssuersQuery.allAsObject$.pipe(
            filter((issuers) => Object.keys(issuers).length > 0)
        )
    ]).pipe(
        map(([issuerIds, issuers]) => {
            const currentSelection = this.issuersCtrl.value;
            return issuerIds.map((id) => {
                // if issuer is not found in the list of issuers then use a default issuer object
                const issuer = issuers[id] || {
                    displayName: 'Unknown',
                    accountIssuerShortName: 'Unknown',
                    accountIssuerLongName: 'Unknown',
                    accountIssuerId: null
                };
                return {
                    title:
                        issuer.displayName ||
                        issuer.accountIssuerShortName ||
                        issuer.accountIssuerLongName,
                    outline: !currentSelection.includes(issuer.accountIssuerId),
                    data: issuer
                };
            });
        })
    );

    _availableInstrumentTypes$ = this._instrumentsQuery
        .selectAvailableInstrumentTypes$({
            type: {
                nin: [
                    'COMPANY',
                    'CASH',
                    'INDEX',
                    'PE_CONVERTIBLE',
                    'SUBSCRIPTIONRIGHT',
                    'FXFORWARD',
                    'FXSPOT',
                    'CONVERTIBLE',
                    'FUTURE'
                    //'ETF'
                ]
            }
        })
        .pipe(shareReplay(1));

    availableInstrumentTypes$ = combineLatest([
        this._availableInstrumentTypes$,
        this._translateService.currentLoader.getTranslation('en')
    ]).pipe(
        debounceTime(0),
        map(([types, translations]) => {
            const currentSelection = this.filterInstrumentTypeCtrl.value;
            return types.map(
                (type): Badge => ({
                    title: translations[type] || type || 'Not set', // fallback to type if translation is not available
                    outline: !currentSelection.includes(type),
                    data: type
                })
            );
        })
    );

    filterInstrumentTypes($event: BadgesChangeEvent<InstrumentType>) {
        if ($event.type === 'toggle-active') {
            this.filterInstrumentTypeCtrl.setValue(
                $event.badges.filter((badge) => !badge.outline).map((badge) => badge.data)
            );
            this.saveFilter({
                setting: 'INSTRUMENT_TYPE_FILTER',
                filter: this.filterInstrumentTypeCtrl.value
            });
        } else if ($event.type === 'not-allowed') {
            this._toastService.show({
                intent: 'info',
                message: $event.message,
                duration: 3000
            });
        }
    }

    filterIssuers($event: BadgesChangeEvent<AccountIssuer>) {
        if ($event.type === 'toggle-active') {
            this.issuersCtrl.setValue(
                $event.badges
                    .filter((badge) => !badge.outline)
                    .map((badge) => badge.data.accountIssuerId)
            );
            this.saveFilter({
                setting: 'ISSUER_FILTER',
                filter: this.issuersCtrl.value
            });
        } else if ($event.type === 'not-allowed') {
            this._toastService.show({
                intent: 'info',
                message: $event.message,
                duration: 3000
            });
        }
    }

    /* TODO: remove once global filter for legal entities is implemented and released
    filterLegalEntities($event: BadgesChangeEvent<LegalEntity>) {
        if ($event.type === 'toggle-active') {
            this.legalEntitiesCtrl.setValue(
                $event.badges
                    .filter((badge) => !badge.outline)
                    .map((badge) => badge.data.ownerId)
            );
            this.saveFilter({
                setting: 'LEGAL_ENTITY_FILTER',
                filter: this.legalEntitiesCtrl.value
            });
        }
    }*/

    showFilters = false;

    baseCurrency$ = this._userSettingsQuery.selectGlobalSetting$('BASE_CURRENCY');
    currentPage$: Observable<unknown>;
    trackByFn = (index: number, item: any) => item.id;

    toggleShowFilters() {
        this.showFilters = !this.showFilters;
    }

    saveFilter({
        setting,
        filter
    }: {
        setting: keyof ComponentSettings;
        filter:
            | Array<LegalEntity['ownerId']>
            | Array<Instrument['type']>
            | Array<Acct['accountType']>
            | Array<AccountIssuer['accountIssuerId']>;
    }) {
        this._userSettingsService
            .saveComponentSetting(setting, filter, 'sb-top-holdings', true)
            .subscribe();
    }

    onAnimationStart($event: any) {}

    onAnimationDone($event: any) {}

    ngOnInit() {
        combineLatest([
            this._userSettingsQuery.selectComponentSetting$(
                'INSTRUMENT_TYPE_FILTER',
                'sb-top-holdings'
            ),
            this._availableInstrumentTypes$.pipe(filter((types) => types.length > 0))
        ])
            .pipe(
                debounceTime(0),
                tap(([persisted, available]) => {
                    if (persisted && persisted.length > 0) {
                        this.filterInstrumentTypeCtrl.setValue(persisted);
                    } else {
                        this.filterInstrumentTypeCtrl.setValue(available);
                    }
                }),
                take(1),
                takeUntil(this._unsubscribe$)
            )
            .subscribe();

        this.availableLegalEntities$
            .pipe(filter((types) => types.length > 0))

            .pipe(
                debounceTime(0),
                tap((available) => {
                    this.legalEntitiesCtrl.setValue(
                        available.map((legalEntity) => legalEntity.data.ownerId)
                    );
                }),
                take(1),
                takeUntil(this._unsubscribe$)
            )
            .subscribe();

        combineLatest([
            this._userSettingsQuery.selectComponentSetting$(
                'ISSUER_FILTER',
                'sb-top-holdings'
            ),
            this.availableIssuers$.pipe(filter((types) => types.length > 0))
        ])
            .pipe(
                debounceTime(0),
                tap(([persisted, available]) => {
                    if (persisted && persisted.length > 0) {
                        this.issuersCtrl.setValue(persisted);
                    } else {
                        this.issuersCtrl.setValue(
                            available.map((issuer) => issuer.data.accountIssuerId)
                        );
                    }
                }),
                take(1),
                takeUntil(this._unsubscribe$)
            )
            .subscribe();

        /*combineQueries([
        this._accountsQuery
            .selectAccountsByFilter$({
                accountType: {
                    ne: AccountTypeEnum.BROKER
                }
            })
            .pipe(
                filter((accounts) => accounts.length > 0),
                take(1),
                map((accounts) => accounts.map((account) => account.accountId))
            ),
        this.baseCurrency$
    ])
        .pipe(
            switchMap(([accountIds, baseCurrency]) => {
                return this._positionsService.get({
                    accountIds,
                    includeNetted: true,
                    baseCurrency
                });
            }),
            takeUntil(this._unsubscribe$)
        )
        .subscribe();*/
    }
    ngOnDestroy() {
        this._unsubscribe$.next(true);
        this._unsubscribe$.complete();
    }

    /** navigateByUrl - navigate to a route
     * @description this method should not be needed but angular routerLink does not support navigating auxiliary routes and main routes at the same time (probably a bug)
     * @param route - route to navigate to
     */
    navigateByUrl(route) {
        this._router.navigateByUrl(route);
    }
}
